import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDisputes } from '../../services/api';
import { extractListData } from '../../utils/apiHelpers';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import Alert from '../../components/common/Alert';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const Disputes = ({ admin }) => {
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    search: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchDisputes();
  }, [filters]);

  const fetchDisputes = async () => {
    try {
      setLoading(true);
      const response = await getDisputes(filters);
      const { items, pagination } = extractListData(response);
      setDisputes(items);
      setPagination(pagination);
    } catch (err) {
      Alert.error(err.message || 'Failed to fetch disputes');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleViewDispute = (disputeId) => {
    navigate(`/admin/disputes/${disputeId}`);
  };

  const getCategoryLabel = (category) => {
    const categories = {
      'item_not_as_described': 'Item Not as Described',
      'item_not_received': 'Item Not Received',
      'damaged_item': 'Damaged Item',
      'wrong_item': 'Wrong Item',
      'seller_unresponsive': 'Seller Unresponsive',
      'delivery_issue': 'Delivery Issue',
      'quality_issue': 'Quality Issue',
      'other': 'Other',
      'buyer_non_payment': 'Buyer Non-Payment',
      'buyer_unresponsive': 'Buyer Unresponsive',
      'buyer_cancellation': 'Buyer Cancellation',
      'buyer_harassment': 'Buyer Harassment',
      'meeting_no_show': 'Meeting No-Show',
      'fraudulent_claim': 'Fraudulent Claim'
    };
    return categories[category] || category;
  };

  const columns = [
    {
      header: 'ID',
      accessor: 'dispute_id',
      cell: (row) => `#${row.dispute_id}`
    },
    {
      header: 'Order',
      accessor: 'order_id',
      cell: (row) => (
        <div>
          <p className="font-medium">Order #{row.order_id}</p>
          <p className="text-xs text-gray-500">${row.total_amount || '0'}</p>
        </div>
      )
    },
    {
      header: 'Parties',
      accessor: 'parties',
      cell: (row) => (
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-xs font-medium text-blue-800">
                {row.raised_by_first_name?.[0]}{row.raised_by_last_name?.[0]}
              </span>
            </div>
            <span className="text-sm">
              {row.raised_by_first_name} {row.raised_by_last_name}
            </span>
            <span className="text-xs text-gray-500">(Buyer)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-xs font-medium text-red-800">
                {row.raised_against_first_name?.[0]}{row.raised_against_last_name?.[0]}
              </span>
            </div>
            <span className="text-sm">
              {row.raised_against_first_name} {row.raised_against_last_name}
            </span>
            <span className="text-xs text-gray-500">(Seller)</span>
          </div>
        </div>
      )
    },
    {
      header: 'Listing',
      accessor: 'listing_title',
      cell: (row) => (
        <div className="max-w-xs">
          <p className="font-medium truncate">{row.listing_title}</p>
          <p className="text-xs text-gray-500 truncate">{row.listing_description}</p>
        </div>
      )
    },
    {
      header: 'Category',
      accessor: 'category',
      cell: (row) => (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
          {getCategoryLabel(row.category)}
        </span>
      )
    },
    {
      header: 'Status',
      accessor: 'status',
      cell: (row) => <StatusBadge status={row.status} type="dispute" />
    },
    {
      header: 'Days Open',
      accessor: 'days_open',
      cell: (row) => (
        <span className={`font-medium ${
          row.days_open > 7 ? 'text-red-600' : 
          row.days_open > 3 ? 'text-yellow-600' : 
          'text-green-600'
        }`}>
          {row.days_open || 0} days
        </span>
      )
    },
    {
      header: 'Created',
      accessor: 'created_at',
      cell: (row) => row.formatted_date || new Date(row.created_at).toLocaleDateString()
    },
    {
      header: 'Actions',
      accessor: 'actions',
      cell: (row) => (
        <div className="flex space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleViewDispute(row.dispute_id)}
          >
            View
          </Button>
        </div>
      )
    }
  ];

  if (loading && disputes.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dispute Management</h1>
          <p className="text-gray-600">Manage order disputes and resolutions</p>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate('/admin/disputes/stats')}
        >
          View Statistics
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_review">In Review</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <option value="">All Categories</option>
              <option value="item_not_as_described">Item Not as Described</option>
              <option value="item_not_received">Item Not Received</option>
              <option value="damaged_item">Damaged Item</option>
              <option value="wrong_item">Wrong Item</option>
              <option value="seller_unresponsive">Seller Unresponsive</option>
              <option value="delivery_issue">Delivery Issue</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search disputes..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={() => setFilters({
                status: '',
                category: '',
                search: '',
                page: 1,
                limit: 20
              })}
              className="w-full"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Disputes</p>
          <p className="text-2xl font-bold text-blue-600">{pagination.total || 0}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">
            {disputes.filter(d => d.status === 'pending').length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">In Review</p>
          <p className="text-2xl font-bold text-orange-600">
            {disputes.filter(d => d.status === 'in_review').length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500"> 7 Days</p>
          <p className="text-2xl font-bold text-red-600">
            {disputes.filter(d => d.days_open > 7).length}
          </p>
        </div>
      </div>

      {/* Disputes Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <DataTable
          columns={columns}
          data={disputes}
          pagination={pagination}
          onPageChange={(page) => handleFilterChange('page', page)}
          searchable={false}
          emptyMessage="No disputes found"
        />
      </div>
    </div>
  );
};

export default Disputes;